import type { SupportedLocale } from './index';

// Single source of truth for the language selector's display data.
//
// Extracted out of Header.vue so the searchable LanguageCombobox and any other
// consumer share ONE map per concern (DRY). Everything here is keyed by
// `SupportedLocale`, so the TypeScript compiler forces every registered locale
// to have an endonym, a flag and an English name — the maps can never silently
// drift out of sync with `@/i18n` `supportedLocales` (NFR-001 KISS).

// Human-readable endonyms (the language's own name for itself). Endonyms are
// language-invariant, so a static map is the simplest source of truth — no i18n
// key per locale needed.
export const localeEndonyms: Record<SupportedLocale, string> = {
  en: 'English',
  fi: 'Suomi',
  sv: 'Svenska',
  no: 'Norsk',
  da: 'Dansk',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  nl: 'Nederlands',
  he: 'עברית',
  zh: '中文',
  ja: '日本語',
  pl: 'Polski',
  ro: 'Română',
  bg: 'Български',
  ru: 'Русский',
};

// Flag emoji per locale — text glyphs, not bundled image assets, so they honour
// the "no images in the distribution" rule. Country chosen by the language's
// primary market. English is shown as a combined GB+US glyph (half UK, half US)
// since the UI English serves both British and American audiences. Note: Windows
// does not render regional-indicator flag emoji and will show the 2-letter code
// instead — a known platform limit.
export const localeFlags: Record<SupportedLocale, string> = {
  en: '🇬🇧🇺🇸',
  fi: '🇫🇮',
  sv: '🇸🇪',
  no: '🇳🇴',
  da: '🇩🇰',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  it: '🇮🇹',
  nl: '🇳🇱',
  he: '🇮🇱',
  zh: '🇨🇳',
  ja: '🇯🇵',
  pl: '🇵🇱',
  ro: '🇷🇴',
  bg: '🇧🇬',
  ru: '🇷🇺',
};

// English language names — the third searchable field (so "bul" → Bulgarian,
// "dut" → Dutch). A small static map keeps the build deterministic and avoids
// Intl edge-cases; `Intl.DisplayNames('en')` is the runtime fallback for any
// future locale code missing from the map, so the data stays auto-extending
// (FR-006) without ever shipping a hardcoded enumeration of the language list.
const englishDisplay: Intl.DisplayNames | null = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language' });
  } catch {
    return null;
  }
})();

const STATIC_ENGLISH_NAMES: Partial<Record<SupportedLocale, string>> = {
  en: 'English',
  fi: 'Finnish',
  sv: 'Swedish',
  no: 'Norwegian',
  da: 'Danish',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  it: 'Italian',
  nl: 'Dutch',
  he: 'Hebrew',
  zh: 'Chinese',
  ja: 'Japanese',
  pl: 'Polish',
  ro: 'Romanian',
  bg: 'Bulgarian',
  ru: 'Russian',
};

export const englishNameOf = (code: SupportedLocale): string =>
  STATIC_ENGLISH_NAMES[code] ?? englishDisplay?.of(code) ?? code;
